import { useEffect, useState } from "react";
import { Alert, Button, Card, Space, Table } from "antd";
import {
  useGetDistributorKycInventoryQuery,
} from "../../../api/admin/distributors/distributorKycApi";
import {
  InventoryKycDetails,
  InventoryKycUpload,
  KycStatusTag,
} from "./InventoryKyc";
import type { KycInventoryRow } from "./kyc";

export default function DistributorKycPanel({
  distributorId,
}: {
  distributorId: string;
}) {
  const { currentData, isFetching, error, refetch } =
    useGetDistributorKycInventoryQuery(distributorId);
  const [selected, setSelected] = useState<KycInventoryRow | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const inventory = currentData?.inventory || [];
  const processing = inventory.some((row) => row.kycStatus === "processing");

  useEffect(() => {
    if (!processing) {
      return;
    }
    const timer = setInterval(() => {
      void refetch();
    }, 5000);
    return () => clearInterval(timer);
  }, [processing, refetch]);

  async function refresh() {
    await refetch();
  }

  return (
    <Card
      title="Distributor KYC submissions"
      extra={
        <Space>
          <Button onClick={refresh} loading={isFetching}>
            Refresh
          </Button>
          <Button
            type="primary"
            disabled={!currentData || Boolean(error)}
            onClick={() => setUploadOpen(true)}
          >
            Upload KYC
          </Button>
        </Space>
      }
    >
      {error && (
        <Alert type="error" message="Unable to load distributor inventory" />
      )}
      <Table<KycInventoryRow>
        rowKey="iccid"
        dataSource={inventory}
        loading={isFetching}
        scroll={{ x: 900 }}
        columns={[
          { title: "ICCID", dataIndex: "iccid" },
          { title: "Product code", dataIndex: "simplyProductCode" },
          { title: "Order", dataIndex: "orderId" },
          { title: "Status", dataIndex: "status" },
          { title: "Arrival date", dataIndex: "arrivalDate" },
          {
            title: "KYC submission",
            render: (_, row) => <KycStatusTag status={row.kycStatus} />,
          },
          {
            title: "Details",
            render: (_, row) => (
              <Button disabled={!row.iccid} onClick={() => setSelected(row)}>
                Details
              </Button>
            ),
          },
        ]}
      />
      {selected && (
        <InventoryKycDetails
          key={selected.iccid}
          distributorId={distributorId}
          item={selected}
          onClose={() => setSelected(null)}
          onSubmitted={refresh}
        />
      )}
      {uploadOpen && (
        <InventoryKycUpload
          distributorId={distributorId}
          inventory={inventory}
          onClose={() => setUploadOpen(false)}
          onSubmitted={refresh}
        />
      )}
    </Card>
  );
}
